import { useState, useEffect, useRef, useMemo } from "react";
import { db } from "../firebase/config";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";

const REGISTRY_DOC = doc(db, "appRegistry", "main");

// Starting column set — only used the very first time this loads (before
// any data exists in Firestore). After that, the columns actually stored
// in Firestore are the source of truth, and can be freely added to or
// removed from below.
const DEFAULT_COLUMNS = [
  "Sl No", "Google account", "App Name", "In Use", "Plan", "Path",
  "Android App", "iOS App", "Firebase Admin Key path", "Live",
  "url1", "url2", "custom url 1", "custom url 2",
  "firebase db", "firebase authentication", "firebase hosting"
];

const cellStyle = {
  padding: "8px",
  border: "1px solid #2a2d3a",
  fontSize: 12.5,
};

const inputStyle = {
  width: "100%",
  padding: "6px",
  border: "none",
  background: "transparent",
  color: "#e6e9f2",
  fontSize: 12.5,
  whiteSpace: "normal",
  wordBreak: "break-word",
  resize: "none",
  fontFamily: "inherit",
  lineHeight: 1.4,
  overflow: "hidden",
};

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  const parseLine = (line) => {
    const cells = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
        else if (ch === '"') { inQuotes = false; }
        else { cur += ch; }
      } else {
        if (ch === '"') inQuotes = true;
        else if (ch === ",") { cells.push(cur); cur = ""; }
        else cur += ch;
      }
    }
    cells.push(cur);
    return cells;
  };
  const header = parseLine(lines[0]).map((h) => h.trim());
  const rows = lines.slice(1).map((line) => {
    const values = parseLine(line);
    const row = {};
    header.forEach((h, i) => { row[h] = values[i] || ""; });
    return row;
  });
  return { header, rows };
}

export default function AppRegistryScreen() {
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedAt, setSavedAt] = useState(null);
  const fileInputRef = useRef(null);

  // Excel-style autofit: each column's width is sized to its longest
  // value (header or any row), in a rough px-per-character estimate,
  // clamped between a sensible minimum and maximum so one very long
  // value can't blow out the whole table.
  const columnWidths = useMemo(() => {
    const widths = {};
    columns.forEach((col) => {
      const longest = Math.max(
        col.length,
        ...rows.map((r) => (r[col] || "").length)
      );
      widths[col] = Math.min(420, Math.max(90, longest * 7.2 + 24));
    });
    return widths;
  }, [columns, rows]);

  const totalTableWidth = Object.values(columnWidths).reduce((sum, w) => sum + w, 0) + 50; // +50 for the delete-row column

  useEffect(() => {
    const unsubscribe = onSnapshot(REGISTRY_DOC, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setColumns(data.columns && data.columns.length ? data.columns : DEFAULT_COLUMNS);
        setRows(data.rows || []);
      } else {
        setColumns(DEFAULT_COLUMNS);
        setRows([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  async function persist(nextColumns, nextRows) {
    await setDoc(REGISTRY_DOC, {
      columns: nextColumns,
      rows: nextRows,
      updatedOn: serverTimestamp(),
    });
    setSavedAt(new Date());
  }

  function updateCell(rowIdx, col, value) {
    const nextRows = rows.map((r, i) => (i === rowIdx ? { ...r, [col]: value } : r));
    setRows(nextRows);
    persist(columns, nextRows);
  }

  function addRow() {
    const newRow = {};
    columns.forEach((c) => (newRow[c] = ""));
    if (columns.includes("Sl No")) newRow["Sl No"] = String(rows.length + 1);
    const nextRows = [...rows, newRow];
    setRows(nextRows);
    persist(columns, nextRows);
  }

  function removeRow(rowIdx) {
    if (!window.confirm("Remove this row?")) return;
    const nextRows = rows.filter((_, i) => i !== rowIdx);
    setRows(nextRows);
    persist(columns, nextRows);
  }

  function addColumn() {
    const name = window.prompt("New column name:");
    if (!name || columns.includes(name)) return;
    const nextColumns = [...columns, name];
    const nextRows = rows.map((r) => ({ ...r, [name]: "" }));
    setColumns(nextColumns);
    setRows(nextRows);
    persist(nextColumns, nextRows);
  }

  function removeColumn(col) {
    if (columns.length <= 1) return;
    if (!window.confirm(`Remove column "${col}"? This deletes that data for every row.`)) return;
    const nextColumns = columns.filter((c) => c !== col);
    const nextRows = rows.map((r) => {
      const { [col]: _omit, ...rest } = r;
      return rest;
    });
    setColumns(nextColumns);
    setRows(nextRows);
    persist(nextColumns, nextRows);
  }

  function handleImportClick() {
    fileInputRef.current?.click();
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const { header, rows: importedRows } = parseCsv(reader.result);
        if (window.confirm(`Import ${importedRows.length} row(s) and ${header.length} column(s) from this CSV? This replaces the current table.`)) {
          setColumns(header);
          setRows(importedRows);
          persist(header, importedRows);
        }
      } catch (err) {
        alert("Could not read CSV file: " + err.message);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  if (loading) {
    return <div style={{ padding: 24, color: "#aab2c5" }}>Loading App Registry…</div>;
  }

  return (
    <div style={{ padding: 20, color: "#e6e9f2" }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 18 }}>App Registry</h2>
      <p style={{ margin: "0 0 14px", fontSize: 12.5, color: "#8890a4" }}>
        Tracks every app's path, Firebase keys, and URLs in one place. Changes save automatically and sync across browser and the Android app.
        {savedAt && <span style={{ marginLeft: 8, color: "#4fd1e8" }}>Saved {savedAt.toLocaleTimeString()}</span>}
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button
          onClick={addRow}
          style={{ padding: "8px 14px", border: "none", borderRadius: 8, background: "linear-gradient(90deg,#4fd1e8,#9b7bea)", color: "#080b12", fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}
        >
          + Add Row
        </button>
        <button
          onClick={addColumn}
          style={{ padding: "8px 14px", border: "1px solid #2a2d3a", borderRadius: 8, background: "#12151f", color: "#e6e9f2", fontSize: 12.5, cursor: "pointer" }}
        >
          + Add Column
        </button>
        <button
          onClick={handleImportClick}
          style={{ padding: "8px 14px", border: "1px solid #2a2d3a", borderRadius: 8, background: "#12151f", color: "#e6e9f2", fontSize: 12.5, cursor: "pointer" }}
        >
          Import CSV
        </button>
        <input
          type="file"
          accept=".csv,text/csv"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #2a2d3a", borderRadius: 10 }}>
        <table style={{ borderCollapse: "collapse", width: totalTableWidth, tableLayout: "fixed" }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col} style={{ ...cellStyle, width: columnWidths[col], background: "#1a1f2e", color: "#4fd1e8", textTransform: "uppercase", fontSize: 10.5, whiteSpace: "nowrap", position: "relative" }}>
                  {col}
                  {columns.length > 1 && (
                    <button
                      onClick={() => removeColumn(col)}
                      title="Remove column"
                      style={{ position: "absolute", top: 3, right: 4, background: "none", border: "none", color: "#8890a4", cursor: "pointer", fontSize: 11 }}
                    >
                      ✕
                    </button>
                  )}
                </th>
              ))}
              <th style={{ ...cellStyle, background: "#1a1f2e" }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} style={{ background: rowIdx % 2 === 0 ? "#0e111a" : "transparent" }}>
                {columns.map((col) => (
                  <td key={col} style={{ ...cellStyle, width: columnWidths[col], verticalAlign: "top" }}>
                    <textarea
                      ref={(el) => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                      style={{ ...inputStyle, whiteSpace: columnWidths[col] >= 420 ? "normal" : "nowrap" }}
                      value={row[col] || ""}
                      title={row[col] || ""}
                      rows={1}
                      onInput={(e) => {
                        e.target.style.height = "auto";
                        e.target.style.height = e.target.scrollHeight + "px";
                      }}
                      onChange={(e) => updateCell(rowIdx, col, e.target.value)}
                    />
                  </td>
                ))}
                <td style={{ ...cellStyle, textAlign: "center", width: 30, color: "#8890a4", cursor: "pointer" }} onClick={() => removeRow(rowIdx)} title="Remove row">
                  ✕
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
